import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { PageHeader } from "./fordon";
import { HubEditorial, faqSchema } from "@/components/HubEditorial";
import { tvattaHusvagnFaq, tvattaHusvagnSections } from "@/data/nationella-guider-3";
import {
  tvattaHusvagnExtraFaq,
  tvattaHusvagnExtraSections,
  tvattaHusvagnTldr,
  GUIDE_AUTHOR,
  GUIDE_UPDATED,
} from "@/data/nationella-guider-3-extra";
import hero from "@/assets/guider/tvatta-husvagn.jpg";

const URL = "https://www.akhusbilfritid.se/tvatta-husvagn";

const sections = [...tvattaHusvagnSections, ...tvattaHusvagnExtraSections];
const faq = [...tvattaHusvagnFaq, ...tvattaHusvagnExtraFaq];

export const Route = createFileRoute("/tvatta-husvagn")({
  // FAQ-schemat byggs i loadern så guidens textdata hamnar i sidans egen
  // chunk i stället för i initial-bundlen.
  codeSplitGroupings: [["loader", "component"]],
  loader: () => ({ faqLd: faqSchema(faq) }),
  head: ({ loaderData }) => ({
    meta: [
      { title: "Tvätta husvagn: så får du bort svartstrimmorna på 2 h" },
      {
        name: "description",
        content:
          "Kemin som löser de svarta ränderna, varför högtryck spränger tätningarna och de sex stegen vi kör inför varje leverans. Även polering, vax och vintertvätt.",
      },
      {
        name: "keywords",
        content:
          "husvagn tvätt, tvätta husvagn, svarta ränder husvagn, husvagn polering, husvagn oxidation, tvätta husbil",
      },
      { property: "og:type", content: "article" },
      { property: "og:title", content: "Tvätta husvagn: så får du bort svartstrimmorna på 2 h" },
      {
        property: "og:description",
        content:
          "Varför en husvagn inte tvättas som en bil, och vad som faktiskt tar bort det grå utan att förstöra tätningarna.",
      },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          "@id": `${URL}#howto`,
          name: "Tvätta husvagn steg för steg",
          url: URL,
          inLanguage: "sv-SE",
          step: [
            { "@type": "HowToStep", name: "Förskölj", text: "Skölj hela vagnen med lågt tryck uppifrån och ner i skugga på sval yta." },
            { "@type": "HowToStep", name: "Skumma", text: "Lägg på pH-neutralt husvagnsschampo och låt verka 5–8 minuter utan att torka." },
            { "@type": "HowToStep", name: "Tvätta i sektioner", text: "Arbeta i partier om två kvadratmeter med mjuk teleskopborste och skölj varje parti direkt." },
            { "@type": "HowToStep", name: "Taket sist", text: "Tvätta taket från stege eller plattform, aldrig genom att gå på det utan tillverkarens godkännande." },
            { "@type": "HowToStep", name: "Efterbehandla", text: "Torka med mikrofiber, behandla svartstrimmor och vårda gummilister med silikonfritt medel." },
          ],
        }),
      },
      ...(loaderData ? [{ type: "application/ld+json", children: JSON.stringify(loaderData.faqLd) }] : []),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Hem", item: "https://www.akhusbilfritid.se/" },
            { "@type": "ListItem", position: 2, name: "Guider", item: "https://www.akhusbilfritid.se/guider" },
            { "@type": "ListItem", position: 3, name: "Tvätta husvagn", item: URL },
          ],
        }),
      },
    ],
  }),
  component: Page,
});

const facts = [
  { k: "40 cm", v: "minsta avstånd med högtryck — och aldrig mot lister" },
  { k: "2–3 h", v: "en ordentlig tvätt av en familjevagn" },
  { k: "3,5–6 tkr", v: "komplett tvätt med polering och vax hos oss" },
  { k: "Oktober", v: "rätt månad att tvätta — före förvaringen, inte efter" },
];

function Page() {
  return (
    <>
      <PageHeader
        label="Nationell guide · Skötsel"
        title={
          <>
            Tvätta husvagnen — <span className="font-italic-display text-accent">rätt</span> kemi, rätt ordning.
          </>
        }
        sub="Metoden vi använder inför varje leverans: vad som tar bort det grå, vad som tar bort svartstrimmor och vad som tryckt in fukt i innertaket hos andra."
      />

      <section className="container-edge pt-10 md:pt-14">
        <figure className="overflow-hidden rounded-3xl border border-border">
          <img
            src={hero}
            alt="Person tvättar en vit husvagn med mjuk teleskopborste och skum"
            width={1600}
            height={912}
            className="h-auto w-full object-cover"
            decoding="async"
          />
        </figure>
      </section>

      <section className="container-edge pt-10 md:pt-14">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {facts.map((f) => (
            <div key={f.k} className="rounded-3xl border border-border bg-card p-6">
              <p className="font-display text-3xl leading-none text-accent">{f.k}</p>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.v}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="pt-14 md:pt-20">
        <HubEditorial
          eyebrow="Guiden"
          sections={sections}
          faq={faq}
          tldr={tvattaHusvagnTldr}
          updated={GUIDE_UPDATED}
          author={GUIDE_AUTHOR}
        />
      </div>

      <section className="container-edge pb-20 md:pb-24">
        <div className="rounded-3xl border border-border bg-warm p-7 md:p-10">
          <p className="eyebrow">Läs vidare</p>
          <div className="mt-5 grid gap-3 sm:grid-cols-3">
            <Link
              to="/fukttest-husbil"
              className="group flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5 transition hover:border-accent"
            >
              <span className="text-sm font-medium">Fukttest — mätpunkter och gränsvärden</span>
              <ArrowUpRight size={16} className="shrink-0 text-accent transition group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/guider/$slug"
              params={{ slug: "husbil-vinterforvaring" }}
              className="group flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5 transition hover:border-accent"
            >
              <span className="text-sm font-medium">Vinterförvaring — komplett checklista</span>
              <ArrowUpRight size={16} className="shrink-0 text-accent transition group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/husvagnar-till-salu"
              className="group flex items-center justify-between gap-3 rounded-2xl border border-border bg-card p-5 transition hover:border-accent"
            >
              <span className="text-sm font-medium">Husvagnar i lager — tvättade och fukttestade</span>
              <ArrowUpRight size={16} className="shrink-0 text-accent transition group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
